import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Experiment from '@homeaway/react-experiment';

import {PriceDetailsWrapper} from '../../PriceDetailsWrapper/PriceDetailsWrapper';
import {InvoiceSummaries} from '../InvoiceSummaries/InvoiceSummaries';
import {CancelProtection} from '../../CancelProtection/CancelProtection';
import {ServiceFeeSummary} from '../ServiceFeeSummary/ServiceFeeSummary';
import {TripSummaryCard} from '../TripSummaryCard/TripSummaryCard';
import {IpmAdditionalInfo} from '../IpmAdditionalInfo/IpmAdditionalInfo';
import {messageType} from '../../../utils/appConfigData';

import {withErrorBoundary} from '../../ErrorBoundary/ErrorBoundary';
import {FallbackUI} from '../../molecules/FallbackUI/FallbackUI';

import {
    tripPaymentSetPresented,
    TRIP_PAYMENTS,
} from '../../../utils/EDAP/ConversationPayments';
import translate from '../../../utils/translations';
import {getReservationUuidFromStayContextData} from '../../../utils/stayDomain.js';

import {MessagesContext} from '../../context/MessagesContext';
import StayContext from '../../context/StayContext';

import './ConversationPayments.less';

class ConversationPayments extends React.Component {
    static propTypes = {
        fullPhoneNumber: PropTypes.string,
        invoiceDownloadSummaries: PropTypes.arrayOf(PropTypes.shape({})),
        priceDetails: PropTypes.shape({}),
        serviceFeeSummary: PropTypes.shape({}),
        translations: PropTypes.shape({}),
        vascartUrl: PropTypes.string,
    };

    static defaultProps = {
        translations: {},
    };

    componentDidMount() {
        const reservationUuid = getReservationUuidFromStayContextData(
            this.stayContext
        );
        const actionLocation = TRIP_PAYMENTS;
        tripPaymentSetPresented({
            actionLocation,
            reservationUUID: reservationUuid,
        });
    }

    static contextType = StayContext;

    get stayContext() {
        if (!this.context) {
            throw new Error(
                'stayContext must be used within a StayContextProvider'
            );
        }
        return this.context;
    }

    isModified(messages) {
        if (!messages || !messages.length) {
            return false;
        }
        const editReservationMessages = messages.filter(
            (msg) => msg.type === messageType.EDIT_RESERVATION
        );

        return !!editReservationMessages.length;
    }

    render() {
        const {
            arrival,
            arrivalTime,
            backUrl,
            contact,
            conversationId,
            countdownSummary,
            departure,
            departureTime,
            fullPhoneNumber,
            handelBannerClick,
            invoiceDownloadSummaries,
            location,
            ownerAvatar,
            priceDetails,
            propertyId,
            propertyImageC5,
            propertyUrl,
            quoteTotals,
            reservationId,
            reservationUuid,
            serviceFeeSummary,
            systemData,
            translations,
            tripSummary,
            vascartUrl,
        } = this.props;
        const {
            stay: {
                unit: {integratedPropertyManager},
            },
        } = this.stayContext;

        const prices = priceDetails || {};
        const arrivalDate = tripSummary.arrivalDate || arrival;
        const departureDate = tripSummary.departureDate || departure;
        const rightRailClassName = classNames(
            'col-xs-12',
            'col-sm-5',
            'col-md-4',
            'ConversationPayments__rightRail--margin'
        );

        return (
            <div className="ConversationPayments row">
                <div className="col-xs-12 col-sm-7 col-md-8">
                    {systemData?.isDesktop && (
                        <React.Fragment>
                            <TripSummaryCard
                                arrivalDate={arrivalDate}
                                arrivalTime={arrivalTime}
                                backUrl={backUrl}
                                contact={contact}
                                conversationId={conversationId}
                                departureDate={departureDate}
                                departureTime={departureTime}
                                expirationDateTime={
                                    countdownSummary?.expirationDateTim
                                } // Not fixing this typo for now: https://jira.homeawaycorp.com/browse/SE-2801
                                fullPhoneNumber={fullPhoneNumber}
                                handelBannerClick={handelBannerClick}
                                location={location}
                                ownerAvatar={ownerAvatar}
                                propertyId={propertyId}
                                propertyImageC5={propertyImageC5}
                                propertyUrl={propertyUrl}
                                quoteTotals={quoteTotals}
                                reservation={reservationId}
                                reservationUuid={reservationUuid}
                                tripSummary={tripSummary}
                                reservationStatus={
                                    tripSummary.reservationStatus
                                }
                                translations={translations}
                            />
                        </React.Fragment>
                    )}
                    <Experiment name={'vrbo_ipm_static_messaging'}>
                        {({On, Off}) => {
                            return (
                                <>
                                    <On>
                                        <MessagesContext>
                                            {({store}) => {
                                                const {messages} = store;
                                                const isBookingModified =
                                                    this.isModified(messages);
                                                return (
                                                    <>
                                                        <div className="section-with-border margin-bottom-2">
                                                            <PriceDetailsWrapper
                                                                actionLocation={
                                                                    TRIP_PAYMENTS
                                                                }
                                                                isBookingModified={
                                                                    isBookingModified
                                                                }
                                                                isVrboIpmStaticMessaging
                                                                priceDetails={
                                                                    priceDetails
                                                                }
                                                                // Adding this key to make sure Modified payment only shows for Payment screen in case test is on
                                                                shouldShowModifiedPayments
                                                                translations={
                                                                    translations
                                                                }
                                                            />
                                                        </div>
                                                        {integratedPropertyManager && (
                                                            <IpmAdditionalInfo
                                                                isBookingModified={
                                                                    isBookingModified
                                                                }
                                                                translations={
                                                                    translations
                                                                }
                                                            />
                                                        )}
                                                    </>
                                                );
                                            }}
                                        </MessagesContext>
                                    </On>
                                    <Off>
                                        <div className="section-with-border">
                                            <PriceDetailsWrapper
                                                actionLocation={TRIP_PAYMENTS}
                                                priceDetails={priceDetails}
                                                translations={translations}
                                            />
                                        </div>
                                    </Off>
                                </>
                            );
                        }}
                    </Experiment>
                    <div className="th-section-divider with-border-xs" />

                    <InvoiceSummaries
                        invoiceDownloadSummaries={invoiceDownloadSummaries}
                    />
                </div>

                <div className={rightRailClassName}>
                    {vascartUrl && (
                        <div className="conversation-sidebar-section rounded-border hidden-xs">
                            <CancelProtection
                                border
                                cancelCtaLink={vascartUrl}
                                eventAction={'VAS url clicked'}
                                translations={translations}
                            />
                        </div>
                    )}

                    <ServiceFeeSummary
                        checkoutUrl={prices.checkoutUrl}
                        serviceFeeSummary={serviceFeeSummary}
                    />
                </div>
            </div>
        );
    }
}
const ConversationPaymentsWithErrorBoundary = (props) => {
    const {translations} = props;
    const ERROR_BOUNDARY_TEXT = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'error.boundary.message'
    );
    const ERROR_BOUNDARY_TITLE = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'th.inbox.conversation.buttons.payments'
    );
    return withErrorBoundary(
        <ConversationPayments {...props} />,
        'trip-payments',
        <FallbackUI
            errorText={ERROR_BOUNDARY_TEXT}
            title={ERROR_BOUNDARY_TITLE}
        />
    );
};

export {ConversationPaymentsWithErrorBoundary as ConversationPayments};
